<!--
  The viewer: one component for every way media is opened (a set, items in no set, or the frozen
  attachments of a note or message). Each layout places it differently (`layout`: a page, the right
  panel of the split view, or full screen), but it always offers the same things:

  - The current item; ‹ › and the arrow keys step through it; the button at the upper right (or a
    double-click) goes full screen.
  - The note of the item on show sits under the title, for a set or a note's attachments (loose
    items already use it as the title).
  - Below: the set's mini-carousel, kept centred on the current item (earlier ones to its left,
    later ones to its right), then the progression strip when the set belongs to one (sets
    are ordered by set date). The strip swaps the set above it, and "Compare two dates" puts the
    first item of two sets side by side.
  - "Select to compare" turns the set into thumbnails; tick two and "View together".
  - Caregivers edit an item's details (never its bytes), remove it, or open Edit set. An item
    opened on its own (not in a set) can be assigned to a patient, or to a different one.
  In full screen the details sit in a panel over the picture, opened with "Details".
-->
<template>
  <section class="md-sv" :class="`is-${layout}`" :aria-label="title">
    <header class="md-sv-head">
      <button v-if="backLabel" class="md-back" @click="$emit('back')">‹ {{ backLabel }}</button>
      <div class="md-sv-title md-grow">
        <h2>{{ title }}</h2>
        <div class="md-small md-sv-meta">
          <span>{{ patientLabel }}</span>
          <span v-if="current"> · {{ fmt(current.captured_at) }}</span>
          <span v-if="current"> · By {{ current.author_name }}</span>
          <span v-if="current && current.captured_by_name"> · Taken by {{ current.captured_by_name }}</span>
          <span v-if="items.length > 1"> · {{ index + 1 }} of {{ items.length }}</span>
        </div>
        <p v-if="current && current.note && (set || source) && !thumbs" class="md-sv-topnote" :title="current.note">{{ current.note }}</p>
      </div>
      <div class="md-sv-actions">
        <button v-if="items.length > 1" class="md-btn ghost sm" :class="{ on: thumbs }" @click="toggleThumbs">{{ thumbs ? 'Back to the picture' : 'Select to compare' }}</button>
        <button v-if="thumbs" class="md-btn sm" :disabled="selected.length !== 2" @click="viewTogether">View together ({{ selected.length }})</button>
        <button v-if="layout === 'immersive' && current && !thumbs" class="md-btn ghost sm" :class="{ on: drawer }" @click="drawer = !drawer">Details</button>
        <button v-if="set && store.canManage && !frozen" class="md-btn ghost sm" @click="$emit('edit-set', set.id)">Edit set</button>
      </div>
    </header>

    <div v-if="frozen && source" class="md-banner info md-sv-frozen">
      <span>As attached to this {{ source.source_type }}<span v-if="source.set_name"> (from the set “{{ source.set_name }}”)</span>. Notes keep the media they were written with.</span>
      <button v-if="source.set_id" class="md-link" @click="$emit('open-set', source.set_id)">View current set{{ source.set_changed ? ' (changed since)' : '' }}</button>
    </div>

    <div v-if="loading" class="md-sv-empty">Loading…</div>
    <div v-else-if="!items.length" class="md-sv-empty">{{ error || 'No items to show. They may have been removed from the library.' }}</div>

    <template v-else>
      <div v-if="thumbs" class="md-sv-thumbs">
        <p class="md-small md-sv-hint">Tick two items, then “View together”.</p>
        <div class="md-grid">
          <button v-for="(it, i) in items" :key="it.id" class="md-tile" :class="{ selected: selected.includes(it.id) }" @click="toggleSelect(it)">
            <MediaThumb :item="it" :show-visibility="store.canManage" />
            <span class="md-sv-check" :class="{ on: selected.includes(it.id) }">{{ selected.includes(it.id) ? '✓' : '' }}</span>
            <span class="meta"><span class="t">{{ fmt(it.captured_at) }}</span><span class="md-muted">{{ it.note || '—' }}</span></span>
            <span class="md-sv-open" role="button" tabindex="0" @click.stop="openAt(i)" @keydown.enter.stop="openAt(i)">Open</span>
          </button>
        </div>
      </div>

      <div v-else class="md-sv-body">
        <div class="md-sv-main">
          <div ref="stage" class="md-sv-stage" @dblclick="toggleFullscreen">
            <MediaFull :item="current" />
            <button v-if="items.length > 1" class="md-sv-arrow left" aria-label="Previous" @click.stop="step(-1)">‹</button>
            <button v-if="items.length > 1" class="md-sv-arrow right" aria-label="Next" @click.stop="step(1)">›</button>
            <button class="md-sv-full" :title="isFullscreen ? 'Restore' : 'Full screen'" @click.stop="toggleFullscreen">{{ isFullscreen ? '⤡' : '⤢' }}</button>
          </div>

          <div v-if="items.length > 1" ref="carousel" class="md-sv-carousel" aria-label="Items in this set">
            <button v-for="(it, i) in items" :key="it.id" class="md-sv-mini" :class="{ on: i === index }" @click="index = i">
              <MediaThumb :item="it" />
              <span>{{ fmtShort(it.captured_at) }}</span>
            </button>
          </div>
        </div>

        <aside v-show="layout !== 'immersive' || drawer" class="md-sv-info">
          <button v-if="layout === 'immersive'" class="md-iconbtn md-sv-info-x" aria-label="Hide details" @click="drawer = false">×</button>
          <div class="md-row md-sv-tags">
            <span v-if="current.primary_tag" class="md-tag primary">{{ store.primaryTagLabel(current.primary_tag) }}</span>
            <span v-for="t in current.tags" :key="t" class="md-tag">{{ t }}</span>
            <span v-if="current.body_site" class="md-tag">{{ store.bodySiteLabel(current.body_site) }}<template v-if="current.body_site_detail"> · {{ current.body_site_detail }}</template></span>
            <span v-if="current.diagnosis" class="md-tag">{{ current.diagnosis.diagnosis_code }} {{ current.diagnosis.label }}</span>
            <span v-if="current.removed_at" class="md-pill removed">Removed from library</span>
            <span v-else-if="store.canManage" class="md-pill" :class="current.patient_visible ? 'visible' : 'staff'">{{ current.patient_visible ? 'Patient can see' : 'Staff only' }}</span>
          </div>
          <p class="md-sv-note">{{ current.note || 'No note.' }}</p>
          <div v-if="store.canManage && !current.removed_at && !editing" class="md-row">
            <button class="md-btn ghost sm" @click="startEdit">Edit details</button>
            <button v-if="context.kind === 'item'" class="md-btn ghost sm" @click="assignCurrent">{{ current.patient_unique_id ? 'Assign to different patient' : 'Assign to patient' }}</button>
            <button v-if="!frozen" class="md-btn danger sm" @click="removeCurrent">Remove</button>
          </div>
          <div v-if="editing" class="md-sv-edit md-card">
            <MediaTagForm v-model="draft" />
            <div class="md-row" style="justify-content:flex-end">
              <button class="md-btn ghost" @click="editing = false">Cancel</button>
              <button class="md-btn" @click="saveEdit">Save details</button>
            </div>
          </div>
        </aside>
      </div>

      <div v-if="progression && progression.sets.length > 1" class="md-sv-prog">
        <div class="md-row">
          <strong>Set progression:</strong> <span>{{ progression.name }}</span>
          <span class="md-grow" />
          <button class="md-btn ghost sm" :class="{ on: picking }" @click="togglePicking">{{ picking ? `Pick two dates (${picked.length}/2)` : 'Compare two dates' }}</button>
          <button class="md-iconbtn" aria-label="Earlier set" :disabled="setIndex <= 0" @click="pickSet(setIndex - 1)">‹</button>
          <button class="md-iconbtn" aria-label="Later set" :disabled="setIndex >= progression.sets.length - 1" @click="pickSet(setIndex + 1)">›</button>
        </div>
        <div class="md-sv-prog-strip">
          <button v-for="(s, i) in progression.sets" :key="s.id" class="md-sv-prog-set" :class="{ on: !picking && i === setIndex, picked: picked.includes(s.id) }" @click="onStripSet(s, i)">
            <MediaThumb :item="s.cover" :count="s.count" ratio="16 / 10" />
            <span class="t">Set {{ i + 1 }}</span>
            <span class="md-sv-prog-date">{{ fmtDate(s.set_date) }}</span>
          </button>
        </div>
        <p v-if="picking" class="md-small md-sv-hint">Tap two sets. The first item of each is compared.</p>
      </div>
    </template>
  </section>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import Swal from 'sweetalert2';
import { useMediaStore } from '@/stores/mediaStore';
import { getMediaItem, getMediaSet, getProgression, listPatientMedia, listReferencingSources, patientName } from '@/api/mediaApi';
import MediaThumb from './MediaThumb.vue';
import MediaFull from './MediaFull.vue';
import MediaTagForm from './MediaTagForm.vue';

const props = defineProps({
  // { kind: 'set', setId } | { kind: 'item', itemId, list? } | { kind: 'note', src, at? }
  context: { type: Object, required: true },
  layout: { type: String, default: 'page' },
  backLabel: { type: String, default: '' },
});
const emit = defineEmits(['back', 'compare', 'edit-set', 'open-set', 'set-shown']);
const store = useMediaStore();

const loading = ref(true);
const error = ref('');
const items = ref([]);
const index = ref(0);
const set = ref(null);
const source = ref(null);
const progression = ref(null);
const setIndex = ref(0);
const thumbs = ref(false);
const selected = ref([]);
const picking = ref(false);
const picked = ref([]);
const editing = ref(false);
const draft = ref(null);
const drawer = ref(false);
const isFullscreen = ref(false);
const stage = ref(null);
const carousel = ref(null);

const frozen = computed(() => props.context.kind === 'note');
const current = computed(() => items.value[Math.min(index.value, items.value.length - 1)] || null);
const title = computed(() => {
  if (set.value) return set.value.name;
  if (source.value) return `${source.value.type} · ${fmtDate(source.value.postDate.slice(0, 10))}`;
  if (props.context.list === 'loose') return 'Items in no set';
  return current.value && current.value.note ? current.value.note.slice(0, 60) : 'Media';
});
const patientLabel = computed(() => (current.value ? patientName(current.value.patient_unique_id) : ''));

// A list opened from the library: the patient's items in no set, or all their images or videos.
async function itemList(item, list) {
  if (!list) return [item];
  const lib = await listPatientMedia(item.patient_unique_id);
  if (list === 'loose') {
    const inSet = new Set(lib.sets.flatMap((s) => s.items.map((m) => m.media_id)));
    return lib.items.filter((it) => !inSet.has(it.id));
  }
  return lib.items.filter((it) => it.kind === list);
}

async function load() {
  loading.value = true;
  error.value = '';
  const c = props.context;
  set.value = null;
  source.value = null;
  progression.value = null;
  try {
    if (c.kind === 'set') {
      await showSet(c.setId);
    } else if (c.kind === 'item') {
      const item = await getMediaItem(c.itemId);
      items.value = await itemList(item, c.list);
      index.value = Math.max(0, items.value.findIndex((it) => it.id === item.id));
    } else if (c.kind === 'note') {
      const sources = await listReferencingSources(store.patientUid);
      source.value = sources.find((s) => `${s.source_type}:${s.source_id}` === c.src) || null;
      items.value = source.value ? source.value.media : [];
      index.value = Math.min(Number(c.at) || 0, Math.max(0, items.value.length - 1));
    }
  } catch (e) {
    error.value = e.message || 'Could not open that media.';
    items.value = [];
  } finally {
    loading.value = false;
  }
}

async function showSet(setId, keepProgression = false) {
  const keepIndex = set.value && set.value.id === setId ? index.value : 0;
  set.value = await getMediaSet(setId);
  items.value = set.value.media;
  index.value = Math.min(keepIndex, Math.max(0, items.value.length - 1));
  if (!keepProgression) {
    progression.value = set.value.progression_id ? await getProgression(set.value.progression_id) : null;
    if (progression.value) setIndex.value = Math.max(0, progression.value.sets.findIndex((s) => s.id === setId));
  }
}

async function pickSet(i) {
  if (!progression.value || i < 0 || i >= progression.value.sets.length) return;
  setIndex.value = i;
  selected.value = [];
  await showSet(progression.value.sets[i].id, true);
  emit('set-shown', set.value.id);
}
function togglePicking() { picking.value = !picking.value; picked.value = []; }
function onStripSet(s, i) {
  if (!picking.value) { pickSet(i); return; }
  const at = picked.value.indexOf(s.id);
  if (at >= 0) { picked.value.splice(at, 1); return; }
  picked.value.push(s.id);
  if (picked.value.length === 2) {
    const covers = picked.value.map((id) => progression.value.sets.find((x) => x.id === id).cover).filter(Boolean);
    picking.value = false;
    picked.value = [];
    if (covers.length === 2) emit('compare', covers[0].id, covers[1].id);
  }
}

function step(d) {
  if (!items.value.length) return;
  index.value = (index.value + d + items.value.length) % items.value.length;
  editing.value = false;
}
function openAt(i) { index.value = i; thumbs.value = false; }
function toggleThumbs() { thumbs.value = !thumbs.value; editing.value = false; }
function toggleSelect(it) {
  const at = selected.value.indexOf(it.id);
  if (at >= 0) selected.value.splice(at, 1);
  else if (selected.value.length < 2) selected.value.push(it.id);
  else store.say('Two at most: untick one first.');
}
function viewTogether() { if (selected.value.length === 2) emit('compare', selected.value[0], selected.value[1]); }

async function toggleFullscreen() {
  try {
    if (document.fullscreenElement) await document.exitFullscreen();
    else if (stage.value && stage.value.requestFullscreen) await stage.value.requestFullscreen();
  } catch (e) { /* not allowed here (e.g. iOS Safari for non-video elements) */ }
}
function onFsChange() { isFullscreen.value = !!document.fullscreenElement; }

// Keys work wherever focus is on the page, except while typing or inside a dialog over the viewer.
function onKey(e) {
  const t = e.target;
  if (t && (/INPUT|TEXTAREA|SELECT/.test(t.tagName) || t.isContentEditable)) return;
  if (document.querySelector('.swal2-container') || document.querySelector('.md-proto .md-seteditor')) return;
  if (e.key === 'ArrowLeft' && !thumbs.value) step(-1);
  else if (e.key === 'ArrowRight' && !thumbs.value) step(1);
  else if (e.key === 'Escape' && !document.fullscreenElement) emit('back');
}

function startEdit() {
  const c = current.value;
  draft.value = {
    patient_unique_id: c.patient_unique_id, note: c.note || '', primary_tag: c.primary_tag, tags: [...(c.tags || [])],
    body_site: c.body_site, body_site_detail: c.body_site_detail || '', orddiagid: c.orddiagid, patient_visible: c.patient_visible,
    captured_by_unique_id: c.captured_by_unique_id,
  };
  editing.value = true;
}
async function saveEdit() {
  const out = await store.run((api) => api.updateMediaItem(current.value.id, draft.value), { success: 'Details saved.', bump: true });
  if (out) { items.value.splice(index.value, 1, out); editing.value = false; }
}

// Same two steps as Edit set's reassign; an item that is also in a set is refused (would_split).
async function assignCurrent() {
  const it = current.value;
  const others = store.patients.filter((p) => p.unique_ID !== it.patient_unique_id);
  const res = await Swal.fire({
    title: it.patient_unique_id ? 'Assign this item to a different patient' : 'Assign this item to a patient',
    input: 'select',
    inputOptions: Object.fromEntries(others.map((p) => [p.unique_ID, `${p.name} (DOB ${p.date_of_birth})`])),
    inputPlaceholder: 'Choose the patient',
    showCancelButton: true,
    confirmButtonText: 'Continue',
    inputValidator: (v) => (!v ? 'Choose a patient.' : undefined),
  });
  if (!res.isConfirmed) return;
  const sure = await Swal.fire({
    title: 'Are you sure?',
    html: `Are you sure you wish to ${it.patient_unique_id ? 'reassign' : 'assign'} this item from <b>${escapeHtml(patientName(it.patient_unique_id))}</b> to <b>${escapeHtml(patientName(res.value))}</b>?${it.patient_unique_id ? ' This is logged.' : ' It leaves your library and becomes part of the chart.'}`,
    icon: 'warning', showCancelButton: true, confirmButtonText: it.patient_unique_id ? 'Yes, reassign' : 'Yes, assign', cancelButtonText: 'No',
  });
  if (!sure.isConfirmed) return;
  const out = await store.run((api) => api.reassignMedia({ type: 'item', id: it.id }, res.value), { success: (r) => `Item moved to ${r.to_patient_name}.`, bump: true });
  if (!out) return;
  items.value.splice(index.value, 1);
  if (index.value >= items.value.length) index.value = Math.max(0, items.value.length - 1);
  if (!items.value.length) emit('back');
}

async function removeCurrent() {
  const it = current.value;
  const mine = it.author_unique_id === store.viewer.unique_ID;
  const fresh = mine && Date.now() - new Date(it.created_at).getTime() < (store.config.settings.uploader_remove_window_sec || 0) * 1000;
  const res = await Swal.fire({
    title: 'Remove from the library?',
    html: `It disappears from the library and from every set it is in. It is kept as part of the medical record (${store.config.settings.retention_years || 10}-year retention) and stays visible in any note that references it.${fresh ? '' : '<br><br><b>A reason is required</b> (you are not the uploader, or more than 24 h have passed).'}`,
    input: 'text',
    inputPlaceholder: fresh ? 'Reason (optional)' : 'Reason (required)',
    inputValidator: (v) => (!fresh && !String(v || '').trim() ? 'Give a reason.' : undefined),
    showCancelButton: true,
    confirmButtonText: 'Remove',
    confirmButtonColor: '#c0392b',
  });
  if (!res.isConfirmed) return;
  const out = await store.run((api) => api.removeMedia({ type: 'item', id: it.id }, res.value || ''), { success: 'Removed from the library.', bump: true });
  if (!out) return;
  items.value.splice(index.value, 1);
  if (index.value >= items.value.length) index.value = Math.max(0, items.value.length - 1);
  if (!items.value.length) emit('back');
}

// Patient names go into Swal's `html`, which renders markup.
const escapeHtml = (t) => String(t ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const fmt = (iso) => (iso ? new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '');
const fmtShort = (iso) => (iso ? new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '');
const fmtDate = (d) => (d ? new Date(`${d}T12:00:00`).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' }) : '');

watch(() => [index.value, items.value], async ([, list], [, before] = []) => {
  await nextTick();
  const strip = carousel.value;
  const thumb = strip && strip.children[index.value];
  if (!thumb || !strip.scrollTo) return;
  // Only the strip scrolls, never the page. A freshly loaded set jumps to its item; stepping glides.
  const left = thumb.offsetLeft - (strip.clientWidth - thumb.offsetWidth) / 2;
  strip.scrollTo({ left, behavior: list === before ? 'smooth' : 'auto' });
});
// The set the strip just showed is already loaded; only a different target reloads.
watch(() => JSON.stringify(props.context), () => {
  if (props.context.kind === 'set' && set.value && set.value.id === props.context.setId) return;
  selected.value = []; thumbs.value = false; editing.value = false; load();
});
watch(() => store.version, () => { if (!editing.value && set.value) showSet(set.value.id, !!progression.value); });

onMounted(() => {
  load();
  document.addEventListener('fullscreenchange', onFsChange);
  window.addEventListener('keydown', onKey);
});
onBeforeUnmount(() => {
  document.removeEventListener('fullscreenchange', onFsChange);
  window.removeEventListener('keydown', onKey);
});
</script>

<style scoped>
.md-sv { display:flex; flex-direction:column; min-width:0; }
.md-sv > * { flex-shrink:0; }
.md-sv-head { display:flex; align-items:flex-start; gap:12px; flex-wrap:wrap; padding:0 0 12px; }
.md-sv-head .md-back { flex-basis:100%; }
.md-sv-title h2 { font-size:22px; }
.md-sv-meta { color:var(--muted); }
.md-sv-topnote { margin:4px 0 0; max-width:760px; display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden; }
.md-sv-actions { display:flex; gap:8px; flex-wrap:wrap; align-items:center; }
.md-sv-actions .md-btn.on { background:var(--teal); color:#fff; }
.md-sv-frozen { display:flex; flex-wrap:wrap; gap:8px; justify-content:space-between; }
.md-sv-empty { padding:48px; text-align:center; color:var(--muted); }
.md-sv-hint { color:var(--muted); }

.md-sv-body { display:flex; flex-direction:column; gap:12px; }
.md-sv-main { min-width:0; }
.md-sv-stage { position:relative; height:min(58vh, 560px); background:#11181b; border-radius:12px; overflow:hidden; }
.md-sv-stage:fullscreen { height:100vh; border-radius:0; }
.md-sv-arrow { position:absolute; top:50%; transform:translateY(-50%); width:44px; height:44px; border-radius:50%; border:none; background:rgba(255,255,255,.85); font-size:28px; line-height:1; cursor:pointer; }
.md-sv-arrow.left { left:12px; } .md-sv-arrow.right { right:12px; }
.md-sv-full { position:absolute; top:10px; right:10px; width:36px; height:36px; border-radius:8px; border:none; background:rgba(255,255,255,.85); font-size:18px; cursor:pointer; }
/* Half a strip of padding each side lets the first and last item reach the centre too. */
.md-sv-carousel { position:relative; display:flex; gap:8px; overflow-x:auto; padding:10px calc(50% - 56px) 4px; scroll-snap-type:x mandatory; }
.md-sv-mini { flex:0 0 112px; border:2px solid transparent; border-radius:8px; background:#fff; padding:0; cursor:pointer; overflow:hidden; scroll-snap-align:center; font-size:12px; color:var(--muted); }
.md-sv-mini.on { border-color:var(--teal); color:var(--ink); font-weight:600; }
.md-sv-mini span { display:block; padding:3px 4px; }
.md-sv-info { position:relative; }
.md-sv-tags { gap:4px; }
.md-sv-note { margin:8px 0 10px; white-space:pre-wrap; }
.md-sv-edit { padding:14px; margin:8px 0 12px; }

.md-sv-prog { border-top:1px solid var(--line); margin-top:14px; padding:12px 0 4px; }
.md-sv-prog .md-btn.on { background:var(--teal); color:#fff; }
.md-sv-prog-strip { display:flex; gap:10px; overflow-x:auto; padding-top:10px; }
.md-sv-prog-set { flex:0 0 120px; border:2px solid var(--line); border-radius:8px; background:#fff; padding:0 0 6px; cursor:pointer; overflow:hidden; display:flex; flex-direction:column; font-size:12px; text-align:left; color:var(--ink); }
.md-sv-prog-set .t { font-weight:600; padding:4px 6px 0; }
.md-sv-prog-set span { padding:0 6px; }
.md-sv-prog-date { color:var(--muted); }
.md-sv-prog-set.on { border-color:var(--teal); }
.md-sv-prog-set.picked { border-color:var(--teal); box-shadow:0 0 0 3px #bfe0dd; }

.md-sv-thumbs { padding:4px 0 8px; }
.md-sv-check { position:absolute; top:8px; left:8px; width:24px; height:24px; border-radius:6px; border:2px solid #fff; background:rgba(0,0,0,.35); color:#fff; display:flex; align-items:center; justify-content:center; font-weight:700; }
.md-sv-check.on { background:var(--teal); }
.md-sv-open { display:block; padding:0 10px 8px; color:var(--teal); font-weight:600; font-size:13px; }

/* Wide pages put the details beside the picture. */
@media (min-width: 1100px) {
  .md-sv.is-page .md-sv-body { display:grid; grid-template-columns:minmax(0, 1fr) 320px; align-items:start; }
}

/* Viewer first: a dark full-screen stage; details are a panel over the picture. */
.md-sv.is-immersive { height:100%; color:#e8eef0; }
.md-sv.is-immersive .md-sv-head { padding:12px 16px; margin:0; background:rgba(0,0,0,.35); }
.md-sv.is-immersive .md-sv-head .md-back { flex-basis:auto; color:#fff; }
.md-sv.is-immersive .md-sv-meta, .md-sv.is-immersive .md-sv-hint, .md-sv.is-immersive .md-sv-prog-date { color:#b8c4c8; }
.md-sv.is-immersive .md-btn.ghost { background:transparent; color:#fff; border-color:rgba(255,255,255,.5); }
.md-sv.is-immersive .md-btn.ghost.on { background:#fff; color:var(--ink); }
.md-sv.is-immersive .md-sv-frozen { margin:10px 16px 0; }
.md-sv.is-immersive .md-sv-body { flex:1 1 auto; flex-shrink:1; min-height:0; position:relative; display:block; }
.md-sv.is-immersive .md-sv-main { height:100%; display:flex; flex-direction:column; }
.md-sv.is-immersive .md-sv-stage { flex:1 1 auto; height:auto; min-height:240px; border-radius:0; background:transparent; }
.md-sv.is-immersive .md-sv-stage :deep(.md-full) { background:transparent; }
.md-sv.is-immersive .md-sv-carousel { padding:8px calc(50% - 56px); background:rgba(0,0,0,.35); }
.md-sv.is-immersive .md-sv-mini { background:#1d272b; color:#b8c4c8; }
.md-sv.is-immersive .md-sv-mini.on { color:#fff; }
.md-sv.is-immersive .md-sv-info { position:absolute; top:12px; right:12px; bottom:12px; width:min(360px, calc(100% - 24px)); overflow:auto;
  background:#fff; color:var(--ink); border-radius:12px; padding:16px; box-shadow:0 10px 30px rgba(0,0,0,.4); z-index:2; }
.md-sv-info-x { position:absolute; top:8px; right:8px; width:30px; height:30px; }
.md-sv.is-immersive .md-sv-prog { margin:0; padding:10px 16px 14px; background:rgba(0,0,0,.35); border-top-color:rgba(255,255,255,.12); }
.md-sv.is-immersive .md-sv-prog-set { background:#1d272b; color:#e8eef0; border-color:#33434a; }
.md-sv.is-immersive .md-sv-prog-set.on, .md-sv.is-immersive .md-sv-prog-set.picked { border-color:#7fe0d6; }
.md-sv.is-immersive .md-iconbtn { background:transparent; color:#fff; border-color:rgba(255,255,255,.4); }
.md-sv.is-immersive .md-sv-info .md-iconbtn { color:var(--ink); border-color:var(--line); }
.md-sv.is-immersive .md-sv-thumbs { padding:12px 16px; overflow:auto; flex:1 1 auto; flex-shrink:1; min-height:0; }

@media (max-width: 640px) {
  .md-sv-stage { height:46vh; }
  .md-sv-title h2 { font-size:19px; }
}
</style>
